import { useState, useRef, useEffect } from 'react'
import { Image as Img } from 'semantic-ui-react'
import Link from 'next/link'
import { Placeholder } from 'semantic-ui-react'
import { gql, useQuery } from '@apollo/client'
import { Loader } from 'semantic-ui-react'

import styles from './style.module.css'
import ExperienceFilter from './ExpFilter'
import ShareButtonRow from '@components/ShareButtonRow'
import ApplyButton from '@components/ApplyButton'
import { getApplicationLabels } from '@components/MobileApplicationCard'
import DefaultCompany from '@assets/default_company.png'
import { experienceLevels } from '../../constants'
import parse from '@src/helpers/parseHTML'
import ResultsNotFound from '@components/ResultsNotFound'
import MobileJobDescriptionModal from '@components/MobileJobDescriptionModal'

const categories = [
  'Software Engineering',
  'Design',
  'Sales',
  'Marketing',
  'HR',
  'Finance',
]

const departments = [
  'Engineering',
  'Design',
  'Sales',
  'Marketing',
  'Human Resources',
  'Accounting and Finance',
]

const getExperienceLevel = (exp) => {
  return experienceLevels.filter((item) => item.text === exp.label)[0]
}

const filterJobs = (list, exp, cat) => {
  const visibleList = list
    .filter((item) => {
      if (exp === -1 || exp?.label === 'All Experience Levels') return true
      const expLevel = getExperienceLevel(exp)
      return expLevel.value === item.experience
    })
    .filter((item) => {
      return departments[cat] === item.department
    })

  return visibleList
}

const renderDescription = (text) => {
  if (!text) return null

  if (text.indexOf('</') < 0) {
    return text
  }

  return parse(text)
}

export default function FeaturedJobs() {
  const [jobs, setJobs] = useState([])
  const [cat, setCat] = useState(0)
  const [exp, setExp] = useState(-1)
  const [jobIdx, setJobIdx] = useState(0)

  const [visibleJobs, setVisibleJobs] = useState(filterJobs(jobs, exp, cat))

  const { data, loading } = useQuery(gql(getFeaturedJobs), {
    variables: { first: 150 },
  })

  useEffect(() => {
    if (data && !loading) {
      const jobs = data?.getFeaturedJobs?.edges.map((edge) => edge.node) ?? []
      setJobs(jobs)
      setVisibleJobs(filterJobs(jobs, exp, cat))
    }
  }, [data, loading])

  useEffect(() => {
    setVisibleJobs(filterJobs(jobs, exp, cat))
  }, [exp, cat])

  function handleExpChange(idx) {
    setExp(idx)
    setJobIdx(0)
    // setLoading(true)
  }

  function handleSelect(idx) {
    setCat(idx)
    setJobIdx(0)
    // setLoading(true)
  }

  function handleJobSelect(idx) {
    setJobIdx(idx)
  }

  return (
    <div className={styles.container}>
      <Desktop
        onCatSelect={handleSelect}
        onExpFilter={handleExpChange}
        onSelect={handleJobSelect}
        loading={loading}
        cat={cat}
        jobs={visibleJobs}
        jobIdx={jobIdx}
      />
      <Mobile
        onCatSelect={handleSelect}
        onExpFilter={handleExpChange}
        onSelect={handleJobSelect}
        loading={loading}
        cat={cat}
        jobs={visibleJobs}
        jobIdx={jobIdx}
      />
    </div>
  )
}

function Desktop({
  onCatSelect,
  onExpFilter,
  jobs,
  cat,
  jobIdx,
  onSelect,
  loading,
}) {
  const renderCategories = () =>
    categories.map((item, idx) => (
      <div
        className={`${styles.filterButton} ${
          cat === idx ? styles.active : null
        }`}
        onClick={() => onCatSelect(idx)}
        key={item}
      >
        {item}
      </div>
    ))

  const renderJobs = () =>
    jobs.map((job, idx) => (
      <div
        className={`${styles.jobEntry} ${
          idx === jobIdx ? styles.activeJob : null
        }`}
        onClick={() => onSelect(idx)}
        key={job}
      >
        <CoImage job={job} />
        <div className={styles.text}>
          <div className={styles.co}>{job?.companyName}</div>
          <div className={styles.title}>{job?.jobTitle}</div>
        </div>
        <div className={styles.remoteTag}>
          {renderRoleType(job?.roleType)}&nbsp; - &nbsp;Remote
        </div>
      </div>
    ))

  const renderJobDetail = () => {
    const job = jobs[jobIdx]
    let postUrl = `www.lifeshack.com/job/${job?.id}`
    const companyName = job?.companyName
    const jobTitle = job?.jobTitle

    return (
      <div className={styles.jobDetail}>
        <div className={styles.top}>
          <div className={styles.topWrapper}>
            <Logo job={job} />
            <div className={styles.actionRow}>
              <div className={styles.applyWrapper}>
                <div className={styles.jobTitle}>{job?.jobTitle}</div>
                <div className={styles.buttonGroup}>
                  <ApplyButton job={job} size="small" />
                  <div style={{ paddingTop: '4px', paddingLeft: '3px' }}>
                    <ShareButtonRow
                      postUrl={postUrl}
                      companyName={companyName}
                      jobTitle={jobTitle}
                      linkOnly
                    />
                  </div>
                </div>
              </div>
              <div className={styles.labelContainer}>
                {getApplicationLabels(job, false, true)}
              </div>
              <div className={styles.sharable}>
                {/* <div className={styles.shareJob}>
                  <b>Share this job!</b>
                </div>
                <br /> */}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.descriptionWrapper}>
          <div className={styles.jobOverview}>
            {renderDescription(job?.description)}
          </div>
        </div>
      </div>
    )
  }
  const renderMain = () => {
    if (loading) {
      return (
        <div className={styles.loading}>
          <Loader inline="centered" active size="massive" />
        </div>
      )
    }

    if (jobs.length === 0) {
      return (
        <div className={styles.main}>
          <ResultsNotFound
            wrapperStyle={{ border: 'none', boxShadow: 'none' }}
            headerStyle={{ fontSize: '25px' }}
            subheaderStyle={{ fontSize: '20px', marginTop: '10px' }}
            subheaderText="We’ll reach out when there’s a match"
            buttonStyle={{ marginTop: '25px' }}
          />
        </div>
      )
    }

    return (
      <div className={styles.main}>
        <div className={styles.leftColumn}>{renderJobs()}</div>
        <div className={styles.description}>{renderJobDetail()}</div>
      </div>
    )
  }

  return (
    <div className={styles.desktop}>
      <div className={styles.expFilter}>
        <ExperienceFilter handleSelect={onExpFilter} />
      </div>
      <div className={styles.filterRow}>
        {renderCategories()}
        <Link href="/employer/signup" passHref>
          <a className={styles.signIn}>
            <div>
              <button className={styles.signupButton}>Post a Job</button>
            </div>
          </a>
        </Link>
      </div>
      {renderMain()}
    </div>
  )
}

function Mobile({
  onCatSelect,
  onExpFilter,
  jobs: jobsProps,
  cat,
  jobIdx,
  onSelect,
  loading,
}) {
  const topRef = useRef()
  const mainRef = useRef(null)
  const [selected, setSelected] = useState(-1)
  const [scroll, setScroll] = useState(false)

  const [jobs, setJobs] = useState([])

  useEffect(() => {
    setJobs(jobsProps)
  }, [jobsProps])

  const selectedJob = selected === -1 ? null : jobs[selected]

  useEffect(() => {
    if (mainRef?.current) {
      console.log('mainRef: ', mainRef)
      mainRef.current.scrollTop = 0
    }
  }, [selected])

  useEffect(() => {
    setSelected(-1)
    scrollToRef(topRef)
  }, [cat])

  const scrollToRef = (target) => {
    // target.current.scrollIntoView();
  }

  const handleScroll = (event) => {
    const scrollTop = event.currentTarget.scrollTop
    if (scrollTop > 0) {
      setScroll(true)
    } else {
      setScroll(false)
    }
  }

  const handleExpFilter = (val) => {
    setSelected(-1)
    scrollToRef(topRef)
    onExpFilter(val)
  }

  const getSelectedImg = (job) => {
    if (job?.jobLogo) {
      return job?.jobLogo
    }

    if (job?.companyProfile?.image) {
      return job?.companyProfile?.image
    }

    return null
  }

  const SelectedJob = () => {
    const theRef = useRef(null)

    useEffect(() => {
      theRef.current.scrollTop = 0
    }, [selected])

    return (
      <div>
        <div className={styles.jobDetails} ref={theRef}>
          <div
            className={`${styles.backToJobs} ${scroll ? styles.scroll : null}`}
            onClick={() => {
              scrollToRef(topRef)
              setSelected(-1)
            }}
          >
            <i className={styles.arrow}></i>Back to featured jobs
          </div>
          <div className={styles.logo}>
            <CoImage job={selectedJob} height={60} width={60} />
            <h3>{`${selectedJob?.jobTitle} @ ${selectedJob?.companyName}`}</h3>
            <div className={styles.details}>
              {getApplicationLabels(selectedJob, false, true)}
            </div>
          </div>
          <div className={styles.description}>
            {renderDescription(selectedJob?.description)}
          </div>
          <div className={styles.shareLabel}>
            <b>Share this job!</b>
          </div>
          <div className={styles.shareWrapper}>
            <ShareButtonRow
              companyName={selectedJob?.companyName}
              jobTitle={selectedJob?.jobTitle}
              postUrl={`www.LifeShack.com/job/${selectedJob?.id}`}
              hideWhatsapp
              hideEmail
              hideReddit
            />
          </div>
          <div className={`${styles.row} ${styles.activeRow}`}></div>
        </div>
      </div>
    )
  }

  const selectJob = (idx) => {
    scrollToRef(topRef)
    setSelected(idx)
  }

  const renderJobs = () => {
    if (loading) {
      return [1, 2, 3, 4, 5].map((job) => (
        <div style={{ padding: '26px 20px 24px', margin: 'auto' }} key={job}>
          <Placeholder>
            <Placeholder.Header image>
              <Placeholder.Line length="medium" />
              <Placeholder.Line length="medium" />
            </Placeholder.Header>
          </Placeholder>
        </div>
      ))
    }

    if (jobs.length === 0) {
      return (
        <ResultsNotFound
          containerStyle={{ width: '100%' }}
          wrapperStyle={{
            border: 'none',
            boxShadow: 'none',
            height: '100%',
          }}
          headerStyle={{ fontSize: '25px' }}
          subheaderStyle={{ fontSize: '20px', marginTop: '10px' }}
          headerText="No results"
          subheaderText="We’ll reach out when there’s a match"
          buttonStyle={{ marginTop: '25px' }}
        />
      )
    }

    return jobs.map((job, idx) => (
      <div
        className={`${styles.jobEntry} ${
          idx === jobIdx ? styles.activeJob : null
        }`}
        onClick={() => selectJob(idx)}
        key={job}
      >
        <div className={styles.imageWrapper}>
          <CoImage job={job} />
        </div>
        <div className={styles.text}>
          <div className={styles.co}>{job?.companyName}</div>
          <div className={styles.title}>{job?.jobTitle}</div>
        </div>
        <div className={styles.remoteTag}>
          {renderRoleType(job?.roleType)}&nbsp; - &nbsp;Remote
        </div>
      </div>
    ))
  }

  const renderCategories = () =>
    categories.map((item, idx) => (
      <div
        className={`${styles.filterButton} ${
          cat === idx ? styles.active : null
        }`}
        onClick={() => onCatSelect(idx)}
        key={item}
      >
        {item}
      </div>
    ))

  return (
    <div className={styles.mobileWrap}>
      <h3>Featured jobs by category</h3>
      <div className={styles.topRow}>
        <div className={styles.topRowContentWrapper}>{renderCategories()}</div>
      </div>
      <div className={styles.mobile}>
        <div className={styles.expFilter}>
          <Link href="/employer/signup" passHref>
            <a className={styles.mobileSignIn}>
              <div>
                <button className={styles.signupButton}>Post a Job</button>
              </div>
            </a>
          </Link>
          <ExperienceFilter handleSelect={handleExpFilter} />
        </div>
        <div className={styles.main}>
          <div ref={topRef}>{renderJobs()}</div>
        </div>
      </div>
      {selected !== -1 && (
        <MobileJobDescriptionModal
          selectedJob={jobs[selected]}
          closeModal={() => setSelected(-1)}
        />
      )}
    </div>
  )
}

function Logo({ job }) {
  return (
    <div className={styles.jobLogo}>
      <CoImage job={job} />
      <h3>{job?.companyName}</h3>
    </div>
  )
}

export const CoImage = ({ job, height, width }) => {
  const getImage = (job) => {
    if (job?.jobLogo) {
      return job?.jobLogo
    }

    if (job?.companyProfile?.image) {
      return job?.companyProfile?.image
    }

    return null
  }

  const src = getImage(job)

  const theHeight = height ?? 50
  const theWidth = width ?? 50

  if (src) {
    return (
      <div className={styles.imageWrap}>
        <Img
          alt={`${job?.companyName} logo`}
          className={`${styles.img} ${styles.coImage}`}
          height={theHeight}
          width={theWidth}
          key={job?.jobTitle}
          objectFit="contain"
          src={getImage(job)}
        ></Img>
      </div>
    )
  }

  return (
    <div className={styles.imageWrap}>
      <Img
        alt="right"
        className={`${styles.img} ${styles.coImage}`}
        src={DefaultCompany}
        key={job?.jobTitle}
        height={theHeight}
        width={theWidth}
        objectFit="contain"
      />
    </div>
  )
}

const renderRoleType = (roleType) => {
  if (roleType === 'FULLTIME') {
    return 'Full time'
  } else if (roleType === 'PARTTIME') {
    return 'Part time'
  } else if (roleType === 'CONTRACT') {
    return 'Contract'
  }
}

export const getFeaturedJobs = /* GraphQL */ `
  query GetFeaturedJobs(
    $first: Int
    $after: JobCursorInput
    $last: Int
    $before: JobCursorInput
    $department: String
    $includePaused: Boolean
    $tag: JobTag
  ) {
    getFeaturedJobs(
      first: $first
      after: $after
      last: $last
      before: $before
      department: $department
      includePaused: $includePaused
      tag: $tag
    ) {
      edges {
        node {
          id
          companyId
          roleType
          jobTitle
          department
          experience
          companyName
          description
          aboutUs
          status
          tags
          remotePolicy
          timezonePolicy
          digitalNomadFriendly
          willSponsor
          jobLogo
          scraped
          scrapedUrl
          scrapedSource
          datePosted
          deleted
        }
        cursor {
          id
          companyId
        }
      }
      pageInfo {
        startCursor {
          id
          companyId
        }
        endCursor {
          id
          companyId
        }
        hasNextPage
        hasPreviousPage
      }
    }
  }
`
